import { cn } from "~/lib/utils";
import type { SubtaskProgress as SubtaskProgressModel } from "../subtaskProgress";

export interface SubtaskProgressProps {
  readonly progress: SubtaskProgressModel;
  readonly className?: string;
  /**
   * When false, render the progress ring only (board cards).
   * List/table rows keep the `done/total` label by default.
   */
  readonly showLabel?: boolean;
}

/**
 * Quiet Linear-style subtask completion: thin ring (+ optional `done/total`).
 */
export function SubtaskProgress({ progress, className, showLabel = true }: SubtaskProgressProps) {
  const ratio = progress.total <= 0 ? 0 : Math.min(1, progress.done / progress.total);
  const sweep = ratio * 360;
  const fractionLabel = `${progress.done}/${progress.total}`;
  const accessibleLabel = `${progress.done} of ${progress.total} subtasks`;

  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1 text-muted-foreground",
        showLabel ? "font-mono text-meta tabular-nums md:text-[11px]" : null,
        className,
      )}
      aria-label={accessibleLabel}
      title={accessibleLabel}
    >
      <span
        aria-hidden="true"
        className="size-3 rounded-full"
        style={{
          background: `conic-gradient(var(--muted-foreground) ${sweep}deg, var(--border) 0deg)`,
          mask: "radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))",
          WebkitMask:
            "radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1.5px))",
        }}
      />
      {showLabel ? fractionLabel : null}
    </span>
  );
}
